import { createApp } from 'vue'
import { createPinia, setActivePinia } from 'pinia'
import App from './App.vue'
import router from '@/router'
import './index.css'
import './assets/toast.css' // Import des styles personnalisés pour les notifications
import { useThemeStore } from '@/stores/themeStore'
import '@/services/portfolioService' // Initialise le singleton et enregistre les écouteurs d'événements (ex: dividendes)
import { useLogger } from '@/utils/logger'
import { resolveLayoutFromRoute } from '@/utils/theme'
import { vClickOutside } from '@/directives/click-outside'

// Toast personnalisé supprimé - implémentation directe dans les composants

// Import Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'

const vuetify = createVuetify({
  components,
  directives,
  // Configuration de thème portée depuis main.js
  theme: {
    defaultTheme: 'light',
    themes: {
      light: { dark: false },
      dark: { dark: true }
    }
  }
})

// Axios: configuration centralisée dans '@/config/axios'

const app = createApp(App)
const pinia = createPinia()
setActivePinia(pinia)

app.use(pinia)
app.use(router)
app.use(vuetify)
app.directive('click-outside', vClickOutside)

interface PiniaStoreRegistry {
  _s: Map<string, { $reset?: () => void }>
}

// Écouteur d'événement pour réinitialiser tous les stores (porté depuis main.js)
window.addEventListener('pinia-reset-stores', () => {
  const storeIds = Object.keys(pinia.state.value)
  for (const storeId of storeIds) {
    if (storeId !== 'auth' && storeId !== 'theme') {
      try {
        const store = (pinia as unknown as PiniaStoreRegistry)._s.get(storeId)
        if (store && typeof store.$reset === 'function') {
          store.$reset()
        }
      } catch {
        // Gestion silencieuse de l'erreur lors de la réinitialisation d'un store
      }
    }
  }
})

// Initialiser le logger
const logger = useLogger('Main')

// Initialiser l'authentification et le thème avant le montage
const initializeApp = async () => {
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()
  authStore.initializeAuth()

  const themeStore = useThemeStore()
  try {
    await themeStore.initializeTheme()
    themeStore.applyThemeContext(resolveLayoutFromRoute(router.currentRoute.value.meta))

    if (authStore.isAuthenticated && authStore.accessToken) {
      const { useRealtimeMarketStore } = await import('@/stores/realtimeMarketStore')
      const realtimeMarketStore = useRealtimeMarketStore()
      realtimeMarketStore.initialize()
      logger.debug('Store de marché en temps réel initialisé')
    }
  } catch (error: unknown) {
    logger.error("Erreur lors de l'initialisation du thème:", error)
  }
}

// Service Worker : actif uniquement en production (évite de casser le HMR Vite en dev)
if (import.meta.env.DEV) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister())
    })
  }
  if ('caches' in window) {
    caches.keys().then((names) => {
      names.forEach((name) => {
        if (name.startsWith('kc-portfolio-manager-')) {
          caches.delete(name)
        }
      })
    })
  }
} else if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        logger.debug('Service Worker enregistré avec succès:', registration.scope)
        
        // Vérifier les mises à jour toutes les heures
        setInterval(() => {
          registration.update()
        }, 60 * 60 * 1000) // 1 heure
        
        // Détecter quand un nouveau service worker est disponible
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Un nouveau service worker est installé et prêt
                logger.info('Nouvelle version disponible. Mise à jour automatique...')
                
                // Attendre que le nouveau service worker soit activé
                newWorker.addEventListener('statechange', () => {
                  if (newWorker.state === 'activated') {
                    // Recharger la page pour appliquer les mises à jour
                    window.location.reload()
                  }
                })
              }
            })
          }
        })
        
        // Écouter les messages du service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data && event.data.type === 'SW_UPDATED') {
            logger.info('Service Worker mis à jour:', event.data.cacheVersion)
            // Le service worker a été mis à jour, recharger la page
            window.location.reload()
          }
        })
        
        // Vérifier immédiatement s'il y a une mise à jour disponible
        registration.update().catch((error) => {
          logger.error('Erreur lors de la vérification des mises à jour:', error)
        })
      })
      .catch((error) => {
        logger.error('Échec de l\'enregistrement du Service Worker:', error)
      })
  })
}

initializeApp()
  .then(() => router.isReady())
  .finally(() => {
    app.mount('#app')
  })
